<template>
  <div>
    <v-color-picker
      class="brand-color-picker"
      :model-value="modelValue"
      mode="hex"
      :modes="['hex']"
      :elevation="0"
      hide-inputs
      v-bind="attrs"
      @update:model-value="onUpdate"
    />

    <!-- Brand colors are handed out as hex codes, so let one be pasted
         directly instead of hunting for it on the spectrum. -->
    <BrandTextField
      v-model="hexDraft"
      variant="basic"
      density="compact"
      class="brand-color-picker__hex mt-3"
      :label="$t('component.color_picker.hex.label')"
      :error-messages="hexError ? $t('component.color_picker.hex.error') : []"
      spellcheck="false"
      autocapitalize="off"
      autocomplete="off"
    />
  </div>
</template>

<script setup>
import { ref, watch, useAttrs } from 'vue'
import BrandTextField from '@/components/global/brand/BrandTextField.vue'
import { parseHexInput } from '@/utils/hex-color'

// Length of "#rrggbb" -- where the picker's #rrggbbaa output gets cut back to
// an opaque color. Not used to cap the field: parseHexInput already rejects
// anything longer, and BrandTextField treats `maxlength` as a counter, not a
// limit.
const HEX_LENGTH = 7

const props = defineProps({
  // Hex color string (#rrggbb).
  modelValue: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['update:modelValue'])
const attrs = useAttrs()

const hexDraft = ref(props.modelValue || '')
const hexError = ref(false)

// v-color-picker in hex mode emits an #rrggbbaa string; normalize to #rrggbb.
function onUpdate(value) {
  const hex = typeof value === 'string' ? value.slice(0, HEX_LENGTH) : value
  emit('update:modelValue', hex)
}

// Typing only commits once the draft parses, so a half-typed code ("#7a1")
// does not fight the picker -- but it still surfaces as an error.
watch(hexDraft, draft => {
  const hex = parseHexInput(draft)
  hexError.value = !!draft && !hex
  if (hex && hex !== props.modelValue) emit('update:modelValue', hex)
})

// Picking on the spectrum (or a reset from the parent) refills the field.
watch(() => props.modelValue, value => {
  if (parseHexInput(hexDraft.value) === value) return
  hexDraft.value = value || ''
  hexError.value = false
})

defineOptions({ inheritAttrs: false })
</script>

<script>
export default { inheritAttrs: false }
</script>

<style lang="scss" scoped>
.brand-color-picker {
  border-radius: 12px;
}

.brand-color-picker__hex {
  max-width: 300px;
}
</style>
